import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import useGlobalDataRefresh from "hooks/useGlobalDataRefresh";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftBadge from "components/SoftBadge";
import SoftPagination from "components/SoftPagination";

import { useAuth } from "context/AuthContext";
import { getTasks, getActions, updateTask, getMissionById, getAirports, getEntityById } from "./Users/services/graphqlService";
import {
  extractTasksFromServiceResponse,
  buildTaskDescription,
  formatTaskAge,
  getTaskCategoryPills,
  getTaskCreatedTime,
  sortTasksByNewest,
  taskMissionId,
  taskId,
  canDecideTask,
  taskStatus,
  prettifyLabel,
  parseTaskDataRecord,
  shouldShowTaskStatusBadge,
  isPilotInterestTask,
  buildUpdateTaskInput,
  TASK_LIST_TABS,
  TASK_LIST_TAB_KEYS,
  taskMatchesStatusTab,
  extractLegsFromTask,
  extractLegIdFromTask,
  normalizeMissionLegs,
  legsHaveRouteDetails,
} from "./tasksParse";
import {
  extractPilotProfileIdFromTask,
  extractPilotDisplayNameFromTask,
  resolvePilotProfileFromTask,
} from "./pilotProfileDisplay";
import ViewPilotProfileModal from "./components/ViewPilotProfileModal";
import ViewNonprofitModal from "./Users/components/ViewNonprofitModal";

const TASKS_PAGE_SIZE = 10;

const PILL_BADGE_COLORS = {
  primary: "info",
  secondary: "warning",
};

function CategoryPill({ label, tone = "primary" }) {
  return (
    <SoftBadge
      variant="contained"
      color={PILL_BADGE_COLORS[tone] || PILL_BADGE_COLORS.primary}
      badgeContent={label}
      size="xs"
      container
    />
  );
}

function TaskRowMeta({ age, pills, statusLabel }) {
  const statusLower = String(statusLabel ?? "").toLowerCase();
  const showStatusBadge = shouldShowTaskStatusBadge(statusLabel);
  const statusColor =
    statusLower === "accepted" || statusLower === "approved"
      ? "success"
      : statusLower === "denied" || statusLower === "rejected"
        ? "error"
        : "secondary";

  return (
    <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      {age ? (
        <SoftBox display="flex" alignItems="center" gap={0.5}>
          <Icon sx={{ fontSize: "0.875rem !important", color: "text.secondary" }}>
            schedule
          </Icon>
          <SoftTypography variant="caption" color="secondary" fontWeight="medium">
            {age}
          </SoftTypography>
        </SoftBox>
      ) : null}
      {showStatusBadge ? (
        <SoftBadge
          variant="contained"
          color={statusColor}
          badgeContent={prettifyLabel(statusLabel)}
          size="xs"
          container
        />
      ) : null}
      {pills.map((pill) => (
        <CategoryPill
          key={`${pill.label}-${pill.tone}`}
          label={pill.label}
          tone={pill.tone}
        />
      ))}
    </SoftBox>
  );
}

function TaskActionButton({
  label,
  icon,
  color = "dark",
  variant = "outlined",
  disabled,
  onClick,
  loading,
}) {
  return (
    <SoftButton
      variant={variant}
      color={color}
      size="small"
      disabled={disabled || loading}
      onClick={onClick}
      sx={{
        minWidth: "auto",
        px: 1.25,
        py: 0.5,
        fontSize: "0.75rem",
        fontWeight: 600,
        borderRadius: "0.5rem",
        textTransform: "none",
        whiteSpace: "nowrap",
      }}
    >
      {loading ? (
        <CircularProgress size={14} color="inherit" />
      ) : (
        <>
          <Icon sx={{ fontSize: "1rem !important", mr: 0.35 }}>{icon}</Icon>
          {label}
        </>
      )}
    </SoftButton>
  );
}

function findTaskDecisionAction(actions, decision) {
  const list = Array.isArray(actions) ? actions : [];
  const pattern =
    decision === "accept"
      ? /accept|approve|acceptpilot/i
      : /deny|reject|decline|rejectpilot/i;

  return list.find((action) => {
    const haystack = [action?.type, action?.subType, action?.message, action?.label]
      .filter(Boolean)
      .join(" ");
    return pattern.test(haystack);
  });
}

function normalizeAirportsList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.AirportsList)) return raw.AirportsList;
  return [];
}

function getAirportLabel(airportId, airports) {
  if (!airportId) return "—";
  const id = String(airportId);
  const airport = airports.find(
    (a) => String(a?.airportId ?? a?.id ?? "") === id,
  );
  return airport?.name || airport?.code || id;
}

function isCargoOfferTask(task) {
  const haystack = [
    task?.taskCategory,
    task?.category,
    task?.taskType,
    task?.type,
    task?.subType,
    task?.message,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    haystack.includes("cargooffer") ||
    haystack.includes("cargo offer") ||
    haystack.includes("offeringcargo") ||
    haystack.includes("offering cargo")
  );
}

function unwrapDynamoJsonValue(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  if (Object.prototype.hasOwnProperty.call(value, "S")) return value.S;
  if (Object.prototype.hasOwnProperty.call(value, "N")) return value.N;
  if (Object.prototype.hasOwnProperty.call(value, "BOOL")) return value.BOOL;
  if (Object.prototype.hasOwnProperty.call(value, "NULL")) return null;
  if (Array.isArray(value.L)) return value.L.map(unwrapDynamoJsonValue);
  if (value.M && typeof value.M === "object") {
    return Object.fromEntries(
      Object.entries(value.M).map(([key, entryValue]) => [
        key,
        unwrapDynamoJsonValue(entryValue),
      ]),
    );
  }
  return value;
}

function parseJsonLikeValue(value) {
  let parsed = unwrapDynamoJsonValue(value);
  while (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return null;
    try {
      parsed = unwrapDynamoJsonValue(JSON.parse(trimmed));
    } catch {
      return trimmed;
    }
  }
  return parsed;
}

function normalizeCargoOfferingItem(item) {
  const parsed = parseJsonLikeValue(item);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;

  const description = String(
    parsed.description ?? parsed.name ?? parsed.label ?? "Cargo",
  ).trim();
  const offeredValue =
    parsed.offeredValue ??
    parsed.OfferedValue ??
    parsed.value ??
    parsed.amount ??
    "";

  if (!description && (offeredValue == null || offeredValue === "")) {
    return null;
  }

  return {
    description: description || "Cargo",
    offeredValue,
  };
}

function getTaskCargoOfferings(task) {
  const data = parseTaskDataRecord(task);
  if (!data || typeof data !== "object") return [];

  // Preferred shape: an explicit cargoOfferings/cargoOffering/cargoOffers
  // payload — either a single offering object or an array of them.
  const raw = data.cargoOfferings ?? data.cargoOffering ?? data.cargoOffers;
  const parsed = parseJsonLikeValue(raw);

  if (Array.isArray(parsed)) {
    const items = parsed.map(normalizeCargoOfferingItem).filter(Boolean);
    if (items.length) return items;
  } else {
    const single = normalizeCargoOfferingItem(parsed);
    if (single) return [single];
  }

  // Flat shape (e.g. CargoOffer tasks): sibling fields
  // { offeredCargo: "Cargo", offeredValue: "20" } instead of a nested
  // cargoOfferings object/array.
  const flatCargo = parseJsonLikeValue(data.offeredCargo);
  if (flatCargo != null) {
    if (Array.isArray(flatCargo)) {
      // offeredCargo can itself be a JSON-stringified array of offering
      // objects, e.g. offeredCargo: '[{"description":"Cargo","offeredValue":30}]'.
      const items = flatCargo.map(normalizeCargoOfferingItem).filter(Boolean);
      if (items.length) return items;
    } else if (typeof flatCargo === "object") {
      const single = normalizeCargoOfferingItem(flatCargo);
      if (single) return [single];
    } else {
      const description = String(flatCargo).trim();
      const offeredValue = parseJsonLikeValue(data.offeredValue);
      const hasValue =
        offeredValue != null && String(offeredValue).trim() !== "";
      if (description || hasValue) {
        return [
          {
            description: description || "Cargo",
            offeredValue: hasValue ? offeredValue : "",
          },
        ];
      }
    }
  }

  return [];
}

function getTaskEntityId(task) {
  if (!task || typeof task !== "object") return "";

  const data = parseTaskDataRecord(task) || {};

  const isUsableId = (value) => {
    const parsed = unwrapDynamoJsonValue(value);
    if (parsed == null) return null;
    const str = String(parsed).trim();
    if (!str || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") {
      return null;
    }
    return str;
  };

  const profileEntityId = Array.isArray(task.profiles)
    ? task.profiles[0]?.entityId ?? task.profiles[0]?.EntityId
    : undefined;

  const candidates = [
    task.entityId,
    task.EntityId,
    task.EntityID,
    task.entity_id,
    data.entityId,
    data.EntityId,
    data.EntityID,
    data.entity_id,
    data.organizationEntityId,
    data.organisationEntityId,
    data.orgEntityId,
    data.organizationId,
    data.organisationId,
    // Fall back to the entityId carried on the logged-in profile record —
    // present on most task payloads even when it isn't duplicated at the
    // top level or inside `data`.
    profileEntityId,
  ];

  for (const candidate of candidates) {
    const id = isUsableId(candidate);
    if (id) return id;
  }

  return "";
}

function getTaskEntityName(task, entityNamesById = {}) {
  const entityId = getTaskEntityId(task);
  if (!entityId) return "";

  const data = parseTaskDataRecord(task) || {};
  const fromTask =
    task.entityName ??
    task.organizationName ??
    task.organisationName ??
    data.entityName ??
    data.organizationName ??
    data.organisationName;
  const directName = unwrapDynamoJsonValue(fromTask);
  if (directName != null && String(directName).trim()) {
    return String(directName).trim();
  }

  return entityNamesById[entityId]?.name || "";
}

function TaskOrganizationLine({ task, entityNamesById, onViewOrganization }) {
  const entityId = getTaskEntityId(task);
  if (!entityId) return null;

  const entityName = getTaskEntityName(task, entityNamesById);

  return (
    <SoftBox
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      gap={0.5}
      mt={0.75}
    >
      <SoftBox display="flex" alignItems="center" gap={0.5} minWidth={0}>
        <Icon sx={{ fontSize: "0.875rem !important", color: "text.secondary" }}>
          business
        </Icon>
        <SoftTypography
          variant="caption"
          color="secondary"
          fontWeight="medium"
          sx={{ wordBreak: "break-word" }}
        >
          {entityName || "Loading organization..."}
        </SoftTypography>
      </SoftBox>
      {onViewOrganization ? (
        <SoftTypography
          component="button"
          type="button"
          variant="caption"
          color="info"
          fontWeight="bold"
          onClick={(event) => {
            event.stopPropagation();
            onViewOrganization(entityId, entityName);
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "2px",
            border: "none",
            background: "none",
            padding: 0,
            cursor: "pointer",
            lineHeight: 1,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <Icon sx={{ fontSize: "0.8125rem !important" }}>open_in_new</Icon>
          View organisation
        </SoftTypography>
      ) : null}
    </SoftBox>
  );
}

function TaskCargoOfferDetails({ offerings }) {
  if (!offerings?.length) return null;

  return (
    <SoftBox display="flex" flexWrap="wrap" gap={0.75} mt={1}>
      {offerings.map((item, index) => {
        const hasValue = item.offeredValue !== "" && item.offeredValue != null;
        return (
          <SoftBox
            key={`${item.description}-${index}`}
            display="flex"
            alignItems="center"
            gap={0.625}
            sx={(theme) => ({
              pl: 1,
              pr: hasValue ? 0.625 : 1,
              py: 0.5,
              borderRadius: "0.5rem",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(99, 110, 255, 0.06)",
            })}
          >
            <Icon sx={{ fontSize: "0.9375rem !important", color: "info.main" }}>
              inventory_2
            </Icon>
            <SoftTypography variant="caption" color="dark" fontWeight="medium">
              {item.description}
            </SoftTypography>
            {hasValue ? (
              <SoftBox
                sx={(theme) => ({
                  px: 0.625,
                  py: 0.125,
                  borderRadius: "0.375rem",
                  backgroundColor: theme.palette.badgeColors?.info?.background,
                })}
              >
                <SoftTypography
                  variant="caption"
                  fontWeight="bold"
                  sx={(theme) => ({ color: theme.palette.badgeColors?.info?.text, lineHeight: 1.4 })}
                >
                  {item.offeredValue}
                </SoftTypography>
              </SoftBox>
            ) : null}
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}

function cargoOfferingSignature(offerings) {
  if (!offerings?.length) return "";
  return offerings
    .map(
      (item) =>
        `${String(item.description ?? "").trim().toLowerCase()}:${String(
          item.offeredValue ?? "",
        ).trim()}`,
    )
    .join("|");
}

function getCargoOfferTaskKey(task) {
  if (!isCargoOfferTask(task)) return taskId(task) || null;

  const data = parseTaskDataRecord(task) || {};
  const missionId = taskMissionId(task) || "";
  const profileId = String(
    data.profileId ??
      data.loggedProfileId ??
      task?.loggedProfileId ??
      task?.profileId ??
      "",
  ).trim();
  const offerSignature = cargoOfferingSignature(getTaskCargoOfferings(task));

  if (!missionId) return taskId(task) || null;

  return [
    "cargo-offer",
    missionId,
    profileId || offerSignature || buildTaskDescription(task),
  ].join(":");
}

function shouldReplaceDuplicateCargoOffer(current, candidate) {
  const currentOfferings = getTaskCargoOfferings(current);
  const candidateOfferings = getTaskCargoOfferings(candidate);

  if (!currentOfferings.length && candidateOfferings.length) return true;
  if (currentOfferings.length && !candidateOfferings.length) return false;

  const currentTime = new Date(getTaskCreatedTime(current) || 0).getTime();
  const candidateTime = new Date(getTaskCreatedTime(candidate) || 0).getTime();
  return candidateTime > currentTime;
}

function dedupeCargoOfferTasks(list) {
  const result = [];
  const indexByKey = new Map();

  for (const task of list) {
    if (!isCargoOfferTask(task)) {
      result.push(task);
      continue;
    }

    const key = getCargoOfferTaskKey(task);
    if (!key) {
      result.push(task);
      continue;
    }

    const existingIndex = indexByKey.get(key);
    if (existingIndex == null) {
      indexByKey.set(key, result.length);
      result.push(task);
      continue;
    }

    if (shouldReplaceDuplicateCargoOffer(result[existingIndex], task)) {
      result[existingIndex] = task;
    }
  }

  return result;
}

function TaskDecisionLegsList({
  legs,
  loading,
  airports,
  highlightLegId,
  interestedPilotName,
}) {
  if (loading) {
    return (
      <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
        <CircularProgress size={16} color="info" />
        <SoftTypography variant="caption" color="secondary">
          Loading flight legs…
        </SoftTypography>
      </SoftBox>
    );
  }

  if (!legs?.length) return null;

  return (
    <SoftBox mt={2}>
      <SoftTypography variant="caption" fontWeight="bold" display="block" mb={1}>
        Flight legs
      </SoftTypography>
      <SoftBox
        sx={(theme) => ({
          borderRadius: "0.75rem",
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
        })}
      >
        {legs.map((leg, index) => {
          const isHighlighted =
            Boolean(highlightLegId) &&
            leg.legId &&
            String(leg.legId) === String(highlightLegId);
          const isTargetLeg =
            isHighlighted || (legs.length === 1 && !highlightLegId);
          const route = `${getAirportLabel(leg.startAirport, airports)} → ${getAirportLabel(leg.endAirport, airports)}`;
          const pilotLabel =
            leg.pilotName ||
            (leg.pilotId ? leg.pilotId : null) ||
            (isTargetLeg && interestedPilotName ? interestedPilotName : null) ||
            "Unassigned";

          return (
            <SoftBox
              key={leg.legId || `leg-${index}`}
              px={2}
              py={1.25}
              display="flex"
              alignItems="flex-start"
              gap={1.5}
              sx={(theme) => ({
                borderTop:
                  index > 0 ? `1px solid ${theme.palette.divider}` : "none",
                backgroundColor: isTargetLeg
                  ? theme.palette.mode === "dark"
                    ? "rgba(99, 110, 255, 0.12)"
                    : "rgba(99, 110, 255, 0.08)"
                  : "transparent",
              })}
            >
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                color="secondary"
                sx={{ minWidth: "2.5rem", pt: 0.15 }}
              >
                {index + 1}
              </SoftTypography>
              <SoftBox flex={1} minWidth={0}>
                <SoftTypography variant="body2" color="dark" fontWeight="medium">
                  {route}
                </SoftTypography>
                <SoftTypography variant="caption" color="secondary">
                  Pilot: {pilotLabel}
                </SoftTypography>
              </SoftBox>
              {isTargetLeg ? (
                <SoftBadge
                  variant="contained"
                  color="info"
                  badgeContent={isHighlighted ? "This leg" : "Pilot interest"}
                  size="xs"
                  container
                />
              ) : null}
            </SoftBox>
          );
        })}
      </SoftBox>
    </SoftBox>
  );
}

function TaskListRow({
  task,
  entityNamesById,
  onViewMission,
  onViewPilotProfile,
  onViewOrganization,
  onAccept,
  onDeny,
  decidingTaskId,
  isLast,
}) {
  const description = buildTaskDescription(task);
  const age = formatTaskAge(getTaskCreatedTime(task));
  const pills = getTaskCategoryPills(task);
  const missionId = taskMissionId(task);
  const pilotProfileId = extractPilotProfileIdFromTask(task);
  const pilotName = extractPilotDisplayNameFromTask(task);
  const canViewPilot = Boolean(pilotProfileId) || isPilotInterestTask(task);
  const tid = taskId(task);
  const showDecisions = canDecideTask(task);
  const isDeciding = decidingTaskId && tid && decidingTaskId === tid;
  const statusLabel = taskStatus(task);
  const cargoOfferings = isCargoOfferTask(task) ? getTaskCargoOfferings(task) : [];
  const hasOrgInfo = Boolean(getTaskEntityId(task));
  const hasDetailsPanel = hasOrgInfo || cargoOfferings.length > 0;

  return (
    <SoftBox px={3} py={1.25} pb={isLast ? 1.25 : 0}>
      <SoftBox
        display="flex"
        alignItems={{ xs: "flex-start", lg: "center" }}
        justifyContent="space-between"
        gap={2}
        p={2.5}
        sx={(theme) => ({
          width: "100%",
          flexWrap: { xs: "wrap", lg: "nowrap" },
          borderRadius: "1rem",
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "0 2px 12px rgba(15, 34, 58, 0.05)",
          transition: "box-shadow 0.2s ease, transform 0.2s ease",
          "&:hover": {
            boxShadow: "0 8px 24px rgba(15, 34, 58, 0.08)",
          },
        })}
      >
        <SoftBox display="flex" alignItems="flex-start" gap={1.5} flex={1} minWidth={0}>
          <SoftBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={44}
            height={44}
            borderRadius="md"
            flexShrink={0}
            sx={({ palette, functions: { linearGradient, rgba } }) => {
              const gradient = palette.gradients?.info ?? {};
              return {
                backgroundImage: linearGradient(
                  rgba(gradient.main, 0.15),
                  rgba(gradient.state, 0.08),
                ),
                border: `1px solid ${rgba(gradient.main, 0.2)}`,
                color: palette.info.main,
              };
            }}
          >
            <Icon sx={{ fontSize: "1.35rem !important" }}>notifications_active</Icon>
          </SoftBox>
          <SoftBox flex={1} minWidth={0}>
            <SoftTypography
              variant="button"
              fontWeight="bold"
              color="dark"
              sx={{
                display: "block",
                mb: 0.75,
                lineHeight: 1.55,
                fontSize: "0.9375rem",
              }}
            >
              {description}
            </SoftTypography>
            <TaskRowMeta age={age} pills={pills} statusLabel={statusLabel} />
            {hasDetailsPanel ? (
              <SoftBox
                mt={1.25}
                px={1.5}
                py={1.25}
                display="flex"
                flexDirection="column"
                gap={1}
                sx={(theme) => ({
                  borderRadius: "0.75rem",
                  width: "fit-content",
                  maxWidth: "100%",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(18, 30, 56, 0.025)",
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <TaskOrganizationLine
                  task={task}
                  entityNamesById={entityNamesById}
                  onViewOrganization={onViewOrganization}
                />
                <TaskCargoOfferDetails offerings={cargoOfferings} />
              </SoftBox>
            ) : null}
          </SoftBox>
        </SoftBox>

        <SoftBox
          display="flex"
          alignItems="center"
          justifyContent={{ xs: "flex-start", lg: "flex-end" }}
          flexWrap="wrap"
          gap={0.75}
          flexShrink={0}
          sx={{
            pl: { xs: 0, lg: 2 },
            ml: { xs: 0, lg: "auto" },
            width: { xs: "100%", lg: "auto" },
          }}
        >
          {canViewPilot && (
            <TaskActionButton
              label="View pilot"
              icon="person"
              color="dark"
              disabled={!canViewPilot}
              onClick={() =>
                canViewPilot &&
                onViewPilotProfile(task, pilotProfileId, pilotName)
              }
            />
          )}
          <TaskActionButton
            label="View Mission"
            icon="flight"
            color="info"
            disabled={!missionId}
            onClick={() => missionId && onViewMission(missionId)}
          />
          {showDecisions ? (
            <>
              <TaskActionButton
                label="Accept"
                icon="check"
                color="success"
                variant="gradient"
                disabled={isDeciding}
                loading={isDeciding}
                onClick={() => onAccept(task)}
              />
              <TaskActionButton
                label="Deny"
                icon="close"
                color="error"
                variant="outlined"
                disabled={isDeciding}
                onClick={() => onDeny(task)}
              />
            </>
          ) : null}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

function TasksPage() {
  const { user, isGuest } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState(TASK_LIST_TAB_KEYS.NEW);
  const [currentPage, setCurrentPage] = useState(1);
  const [pilotProfileModal, setPilotProfileModal] = useState(null);
  const [taskActions, setTaskActions] = useState([]);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [decidingTaskId, setDecidingTaskId] = useState(null);
  const [decisionLegs, setDecisionLegs] = useState([]);
  const [decisionLegsLoading, setDecisionLegsLoading] = useState(false);
  const [decisionAirports, setDecisionAirports] = useState([]);
  const [entityNamesById, setEntityNamesById] = useState({});
  const [organizationModal, setOrganizationModal] = useState(null);

  const fetchTasks = useCallback(async () => {
      if (isGuest) {
        setTasks([]);
        setLoadingList(false);
        return;
      }
      if (!user?.id_token) {
        setTasks([]);
        setLoadingList(false);
        return;
      }

      setLoadingList(true);
      setError(null);

      try {
        const res = await getTasks(user.id_token);
        const { list } = extractTasksFromServiceResponse(res);
        setTasks(dedupeCargoOfferTasks(list));
      } catch (err) {
        setTasks([]);
        setError(err?.message || "Failed to load tasks.");
      } finally {
        setLoadingList(false);
      }
    },
    [isGuest, user?.id_token],
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    if (!user?.id_token || isGuest || !tasks.length) return;

    const entityDebugRows = tasks.map((task) => ({
      taskId: taskId(task),
      taskCategory: task?.taskCategory ?? task?.category ?? "",
      taskType: task?.taskType ?? task?.type ?? "",
      entityId: getTaskEntityId(task) || "(missing)",
      loggedProfileName: task?.loggedProfileName ?? "",
      data: parseTaskDataRecord(task),
    }));

    console.groupCollapsed("[Tasks] Entity lookup debug");
    console.table(entityDebugRows);
    console.groupEnd();

    const missingEntityIds = [
      ...new Set(tasks.map(getTaskEntityId).filter(Boolean)),
    ].filter((entityId) => !entityNamesById[entityId]);

    if (!missingEntityIds.length) {
      console.warn(
        "[Tasks] No new entity IDs to fetch. If organization name is missing, the task likely has empty entityId.",
      );
      return;
    }

    console.log("[Tasks] Fetching organization names for entity IDs:", missingEntityIds);

    let cancelled = false;
    Promise.all(
      missingEntityIds.map((entityId) =>
        getEntityById(entityId, user.id_token)
          .then((res) => {
            const entity = res?.data || {};
            console.log("[Tasks] getEntityById result", {
              entityId,
              entity,
            });
            return {
              entityId,
              name: entity.name || entity.title || entity.entityName || entityId,
            };
          })
          .catch((error) => {
            console.error("[Tasks] getEntityById failed", {
              entityId,
              error,
            });
            return { entityId, name: entityId };
          }),
      ),
    ).then((entries) => {
      if (cancelled) return;
      console.log("[Tasks] Entity names cache update", entries);
      setEntityNamesById((prev) => {
        const next = { ...prev };
        entries.forEach((entry) => {
          next[entry.entityId] = { name: entry.name };
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [tasks, user?.id_token, isGuest, entityNamesById]);

  useEffect(() => {
    const task = pendingDecision?.task;
    if (!task || !isPilotInterestTask(task)) {
      setDecisionLegs([]);
      setDecisionLegsLoading(false);
      setDecisionAirports([]);
      return;
    }

    let cancelled = false;
    const fromTask = extractLegsFromTask(task);
    const missionId = taskMissionId(task);
    const needMissionFetch =
      !legsHaveRouteDetails(fromTask) && Boolean(missionId && user?.id_token);

    setDecisionLegsLoading(needMissionFetch);

    const airportsPromise = user?.id_token
      ? getAirports(user.id_token).catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });

    const missionPromise = needMissionFetch
      ? getMissionById(missionId, user.id_token)
      : Promise.resolve(null);

    Promise.all([missionPromise, airportsPromise])
      .then(([missionRes, airportsRes]) => {
        if (cancelled) return;

        setDecisionAirports(normalizeAirportsList(airportsRes?.data));

        if (!needMissionFetch) {
          setDecisionLegs(fromTask);
          return;
        }

        let mission = missionRes?.data;
        while (typeof mission === "string") {
          try {
            mission = JSON.parse(mission);
          } catch {
            break;
          }
        }
        const missionLegs = normalizeMissionLegs(mission);
        setDecisionLegs(missionLegs.length ? missionLegs : fromTask);
      })
      .catch(() => {
        if (!cancelled) {
          setDecisionLegs(fromTask);
          setDecisionAirports([]);
        }
      })
      .finally(() => {
        if (!cancelled) setDecisionLegsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pendingDecision?.task, user?.id_token]);

  useGlobalDataRefresh("task", useCallback(() => {
    fetchTasks();
  }, [fetchTasks]));

  useEffect(() => {
    if (!user?.id_token || isGuest) {
      setTaskActions([]);
      return;
    }

    let cancelled = false;
    Promise.all([
      getActions("Task", user.id_token).catch(() => []),
      getActions("Mission", user.id_token).catch(() => []),
    ]).then(([taskList, missionList]) => {
      if (cancelled) return;
      const merged = [...(taskList ?? []), ...(missionList ?? [])];
      const seen = new Set();
      setTaskActions(
        merged.filter((action) => {
          const key = `${action?.type ?? ""}:${action?.subType ?? ""}:${action?.message ?? ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id_token, isGuest]);

  const tabCounts = useMemo(() => {
    const counts = {};
    TASK_LIST_TABS.forEach(({ key }) => {
      counts[key] = tasks.filter((t) => taskMatchesStatusTab(t, key)).length;
    });
    return counts;
  }, [tasks]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusTab, searchQuery]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (!taskMatchesStatusTab(t, statusTab)) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const haystack = [
          buildTaskDescription(t),
          taskStatus(t),
          t.taskCategory,
          t.taskType,
          formatTaskAge(getTaskCreatedTime(t)),
          taskMissionId(t),
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [tasks, statusTab, searchQuery]);

  const sortedTasks = useMemo(
    () => sortTasksByNewest(filteredTasks),
    [filteredTasks],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sortedTasks.length / TASKS_PAGE_SIZE),
  );
  const effectivePage = Math.min(Math.max(1, currentPage), totalPages);

  useEffect(() => {
    if (currentPage !== effectivePage) {
      setCurrentPage(effectivePage);
    }
  }, [currentPage, effectivePage]);

  const paginatedTasks = useMemo(() => {
    const start = (effectivePage - 1) * TASKS_PAGE_SIZE;
    return sortedTasks.slice(start, start + TASKS_PAGE_SIZE);
  }, [sortedTasks, effectivePage]);

  const entriesStart =
    sortedTasks.length === 0 ? 0 : (effectivePage - 1) * TASKS_PAGE_SIZE + 1;
  const entriesEnd = Math.min(
    effectivePage * TASKS_PAGE_SIZE,
    sortedTasks.length,
  );

  const handleViewMission = (missionId) => {
    if (missionId) {
      navigate(`/dashboard/missions/${missionId}`);
    }
  };

  const handleViewPilotProfile = async (task, _profileId, fallbackName) => {
    if (!user?.id_token) return;

    const resolved = await resolvePilotProfileFromTask(
      task,
      user.id_token,
      getMissionById,
    );

    if (!resolved.profileId) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "warning",
            title: "Pilot not found",
            message:
              "Could not determine which pilot this task refers to. The pilot may need to be identified in task data.",
          },
        }),
      );
      return;
    }

    setPilotProfileModal({
      task,
      profileId: resolved.profileId,
      fallbackName: resolved.name || fallbackName,
    });
  };

  const handleClosePilotProfile = () => {
    setPilotProfileModal(null);
  };

  const handleViewOrganization = (entityId, fallbackName) => {
    if (!entityId) return;
    setOrganizationModal({ entityId, fallbackName });
  };

  const handleCloseOrganizationModal = () => {
    setOrganizationModal(null);
  };

  const submitTaskDecision = async (task, decision) => {
    const tid = taskId(task);
    const missionId = taskMissionId(task);
    if (!user?.id_token || !tid || !missionId) return;

    setDecidingTaskId(tid);
    try {
      let input;

      if (decision === "accept" && isCargoOfferTask(task)) {
        // Cargo offer acceptance uses a fixed action shape (not the
        // generic pilot-interest accept payload). The State-Machine-driven
        // backend needs missionId in `data` (see note below), plus
        // description/acceptedValue/offeringEntity per the required
        // CargoAccepted format.
        const primaryOffer = getTaskCargoOfferings(task)[0] || {};
        const offeringEntityId = getTaskEntityId(task);
        input = {
          message: "CargoAccepted",
          type: "CargoAccepted",
          subType: "CargoAccepted",
          data: [
            // The "Get Mission" state in the CargoAccepted Step Function
            // reads $event.header.missionId, falling back to
            // $event.data.data[name='missionId'].value — the event header
            // this app's backend emits doesn't carry missionId, so it must
            // be included here or that state fails with "returned nothing".
            { name: "missionId", value: String(missionId ?? "") },
            { name: "description", value: String(primaryOffer.description ?? "") },
            { name: "acceptedValue", value: String(primaryOffer.offeredValue ?? "") },
            { name: "offeringEntity", value: String(offeringEntityId ?? "") },
          ],
        };
      } else {
        const actionConfig = findTaskDecisionAction(taskActions, decision);
        const pilotProfileId = (
          await resolvePilotProfileFromTask(task, user.id_token, getMissionById)
        ).profileId;
        input = buildUpdateTaskInput(
          task,
          decision,
          actionConfig,
          pilotProfileId,
        );
      }

      await updateTask(tid, input, user.id_token);
      setTasks((prev) =>
        prev.map((item) => {
          const itemId = taskId(item);
          if (tid && itemId === tid) {
            return {
              ...item,
              status: decision === "accept" ? "Accepted" : "Denied",
              taskStatus: decision === "accept" ? "Accepted" : "Denied",
            };
          }
          return item;
        }),
      );
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "success",
            title: decision === "accept" ? "Task accepted" : "Task denied",
            message:
              decision === "accept"
                ? isCargoOfferTask(task)
                  ? "The cargo offer was accepted."
                  : "The pilot interest was accepted."
                : isCargoOfferTask(task)
                  ? "The cargo offer was denied."
                  : "The pilot interest was denied.",
          },
        }),
      );
      setPendingDecision(null);
      fetchTasks();
    } catch (err) {
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Action failed",
            message: err?.message ?? "Could not update this task.",
          },
        }),
      );
    } finally {
      setDecidingTaskId(null);
    }
  };

  const handleAcceptTask = (task) => {
    setPendingDecision({ task, decision: "accept" });
  };

  const handleDenyTask = (task) => {
    setPendingDecision({ task, decision: "deny" });
  };

  const showEmptyList = !loadingList && tasks.length === 0 && !error;
  const showNoMatches =
    !loadingList && tasks.length > 0 && sortedTasks.length === 0;
  const decisionTask = pendingDecision?.task;
  const isDecisionCargoOffer = isCargoOfferTask(decisionTask);
  const decisionCargoOfferings = isDecisionCargoOffer
    ? getTaskCargoOfferings(decisionTask)
    : [];

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={2}
      >
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="bold" gutterBottom>
            Tasks
          </SoftTypography>
          <SoftTypography variant="body2" color="text" maxWidth={480}>
            Review pilot interest notifications and take action on mission
            tasks assigned to you.
          </SoftTypography>
        </SoftBox>
        <SoftButton
          variant="outlined"
          color="info"
          onClick={() => fetchTasks()}
          disabled={loadingList || isGuest}
          sx={{ borderRadius: "0.75rem" }}
        >
          <Icon sx={{ mr: 0.5 }}>refresh</Icon>
          Refresh
        </SoftButton>
      </SoftBox>

      {error && (
        <SoftBox mb={2}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </SoftBox>
      )}

      {isGuest && (
        <SoftBox mb={2}>
          <Alert severity="info">
            Sign in with a full account to load tasks from the server.
          </Alert>
        </SoftBox>
      )}

      <Card
        sx={{
          overflow: "hidden",
          borderRadius: 2,
          boxShadow: "0 4px 24px rgba(15, 34, 58, 0.06)",
        }}
      >
        {loadingList ? (
          <SoftBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1.5}
            px={3}
            py={6}
          >
            <CircularProgress size={32} color="info" />
            <SoftTypography variant="body2" color="text">
              Loading tasks...
            </SoftTypography>
          </SoftBox>
        ) : showEmptyList ? (
          <SoftBox px={3} py={6} textAlign="center">
            <SoftBox
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              width={64}
              height={64}
              borderRadius="50%"
              mb={2}
              sx={({ palette, functions: { rgba } }) => ({
                backgroundColor: rgba(palette.gradients.info.main, 0.1),
                color: palette.info.main,
              })}
            >
              <Icon sx={{ fontSize: "2rem !important" }}>task_alt</Icon>
            </SoftBox>
            <SoftTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
              No tasks yet
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              New mission notifications will appear here when pilots show
              interest or action is required.
            </SoftTypography>
          </SoftBox>
        ) : (
          <>
            <SoftBox
              sx={(theme) => ({
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "rgba(99, 110, 255, 0.03)",
              })}
            >
              <Tabs
                value={statusTab}
                onChange={(_, value) => setStatusTab(value)}
                sx={{ px: 2, pt: 1.5 }}
              >
                {TASK_LIST_TABS.map(({ key, label }) => (
                  <Tab
                    key={key}
                    value={key}
                    label={`${label} (${tabCounts[key] ?? 0})`}
                  />
                ))}
              </Tabs>
              <SoftBox
                px={3}
                pb={2}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
              >
                <SoftBox
                  sx={{
                    width: 360,
                    maxWidth: "100%",
                    "&:focus-within": {
                      "& .MuiInputBase-root": {
                        border: "2px solid",
                        borderColor: "info.main",
                      },
                    },
                    "& .MuiInputBase-root.Mui-focused": {
                      boxShadow: "none",
                      borderColor: "transparent !important",
                    },
                  }}
                >
                  <SoftInput
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    icon={{ component: "search", direction: "left" }}
                  />
                </SoftBox>
              </SoftBox>
            </SoftBox>

              {showNoMatches ? (
                <SoftBox px={3} py={6} textAlign="center">
                  <Icon sx={{ fontSize: "2.5rem !important", color: "text.secondary", mb: 1 }}>
                    search_off
                  </Icon>
                  <SoftTypography variant="h6" fontWeight="bold" color="dark" gutterBottom>
                    No matching tasks
                  </SoftTypography>
                  <SoftTypography variant="body2" color="text">
                    {searchQuery.trim()
                      ? "Try adjusting your search."
                      : statusTab === TASK_LIST_TAB_KEYS.NEW
                        ? "No new tasks in this tab yet."
                        : statusTab === TASK_LIST_TAB_KEYS.COMPLETED
                          ? "No completed tasks in this tab yet."
                          : "No tasks match this view yet."}
                  </SoftTypography>
                </SoftBox>
              ) : (
                <>
                  <SoftBox component="ul" p={0} m={0} sx={{ listStyle: "none" }}>
                    {paginatedTasks.map((task, index) => (
                      <SoftBox
                        component="li"
                        key={task.taskId ?? task.id ?? index}
                        p={0}
                        m={0}
                      >
                        <TaskListRow
                          task={task}
                          entityNamesById={entityNamesById}
                          onViewMission={handleViewMission}
                          onViewPilotProfile={handleViewPilotProfile}
                          onViewOrganization={handleViewOrganization}
                          onAccept={handleAcceptTask}
                          onDeny={handleDenyTask}
                          decidingTaskId={decidingTaskId}
                          isLast={index === paginatedTasks.length - 1}
                        />
                      </SoftBox>
                    ))}
                  </SoftBox>
                  <SoftBox
                    px={3}
                    py={2.5}
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                    sx={{ borderTop: "1px solid", borderColor: "divider" }}
                  >
                    <SoftTypography
                      variant="button"
                      color="secondary"
                      fontWeight="regular"
                    >
                      Showing {entriesStart} to {entriesEnd} of{" "}
                      {sortedTasks.length}{" "}
                      {sortedTasks.length === 1 ? "task" : "tasks"}
                      {searchQuery.trim() ? " (filtered)" : ""}
                    </SoftTypography>
                    {totalPages > 1 ? (
                      <SoftPagination variant="gradient" color="info">
                        <SoftPagination
                          item
                          onClick={() =>
                            setCurrentPage((page) => Math.max(1, page - 1))
                          }
                          disabled={effectivePage <= 1}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                        </SoftPagination>
                        {totalPages <= 7 ? (
                          Array.from({ length: totalPages }, (_, index) => {
                            const page = index + 1;
                            return (
                              <SoftPagination
                                key={page}
                                item
                                active={page === effectivePage}
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </SoftPagination>
                            );
                          })
                        ) : (
                          <SoftTypography variant="button" fontWeight="medium" mx={1}>
                            Page {effectivePage} of {totalPages}
                          </SoftTypography>
                        )}
                        <SoftPagination
                          item
                          onClick={() =>
                            setCurrentPage((page) =>
                              Math.min(totalPages, page + 1),
                            )
                          }
                          disabled={effectivePage >= totalPages}
                        >
                          <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                        </SoftPagination>
                      </SoftPagination>
                    ) : null}
                  </SoftBox>
                </>
              )}
            </>
          )}
      </Card>

      <ViewNonprofitModal
        open={Boolean(organizationModal)}
        onClose={handleCloseOrganizationModal}
        nonprofitId={organizationModal?.entityId}
        idToken={user?.id_token}
      />

      <ViewPilotProfileModal
        open={Boolean(pilotProfileModal)}
        onClose={handleClosePilotProfile}
        profileId={pilotProfileModal?.profileId}
        idToken={user?.id_token}
        task={pilotProfileModal?.task}
        fallbackName={pilotProfileModal?.fallbackName}
      />

      <Dialog
        open={Boolean(pendingDecision)}
        onClose={() => !decidingTaskId && setPendingDecision(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={36}
              height={36}
              borderRadius="md"
              sx={({ palette }) => ({
                backgroundColor:
                  pendingDecision?.decision === "accept"
                    ? palette.success.main
                    : palette.error.main,
                color: palette.white.main,
              })}
            >
              <Icon fontSize="small">
                {pendingDecision?.decision === "accept" ? "check" : "close"}
              </Icon>
            </SoftBox>
            {pendingDecision?.decision === "accept" ? "Accept task" : "Deny task"}
          </SoftBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDecision?.decision === "accept"
              ? isDecisionCargoOffer
                ? "Accept this cargo offer for the mission?"
                : "Accept this pilot's interest for the mission?"
              : isDecisionCargoOffer
                ? "Deny this cargo offer for the mission?"
                : "Deny this pilot's interest for the mission?"}
          </DialogContentText>
          {pendingDecision?.task ? (
            <SoftBox
              mt={2}
              p={2}
              sx={(theme) => ({
                borderRadius: "0.75rem",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.04)"
                    : "rgba(99, 110, 255, 0.05)",
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              <SoftTypography variant="body2" color="dark" fontWeight="medium">
                {buildTaskDescription(pendingDecision.task)}
              </SoftTypography>
              <TaskOrganizationLine
                task={pendingDecision.task}
                entityNamesById={entityNamesById}
                onViewOrganization={handleViewOrganization}
              />
              <TaskCargoOfferDetails offerings={decisionCargoOfferings} />
            </SoftBox>
          ) : null}
          {pendingDecision?.task && isPilotInterestTask(pendingDecision.task) ? (
            <TaskDecisionLegsList
              legs={decisionLegs}
              loading={decisionLegsLoading}
              airports={decisionAirports}
              highlightLegId={extractLegIdFromTask(pendingDecision.task)}
              interestedPilotName={extractPilotDisplayNameFromTask(
                pendingDecision.task,
              )}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            onClick={() => setPendingDecision(null)}
            disabled={Boolean(decidingTaskId)}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color={pendingDecision?.decision === "accept" ? "success" : "error"}
            onClick={() =>
              pendingDecision &&
              submitTaskDecision(pendingDecision.task, pendingDecision.decision)
            }
            disabled={Boolean(decidingTaskId)}
          >
            {decidingTaskId ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                Submitting…
              </>
            ) : pendingDecision?.decision === "accept" ? (
              "Accept"
            ) : (
              "Deny"
            )}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default TasksPage;
